import { useState } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import { Text } from "../../theme/primitives";
import { formatClock } from "../../lib/time";
import type { ChatMessage } from "../../matrix/timeline";
import { AudioPlayer } from "../voice/AudioPlayer";
import { Reactions, QuickReactRow } from "./ReactionChips";
import { toggleReaction, type ReactionSummary } from "./reactions";

interface MessageBubbleProps {
  client: MatrixClient;
  roomId: string;
  message: ChatMessage;
  read: boolean;
  reactions?: ReactionSummary[];
  /** In group rooms, the sender's "Name · Company" label + a stable color. */
  senderLabel?: { name: string; color: string };
}

/** A single WhatsApp-style message bubble (sent vs received) with reactions. */
export function MessageBubble({ client, roomId, message, read, reactions = [], senderLabel }: MessageBubbleProps) {
  const mine = message.isMine;
  const [hover, setHover] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // The bubble container sets a base color, but <Text> forces its own color,
  // so set it explicitly per side. On the dark (outgoing) bubble the body must
  // be light and the timestamp a translucent light, or it's unreadable.
  const bodyColor = mine ? "var(--color-bubble-out-text)" : "var(--color-bubble-in-text)";
  const metaColor = mine ? "rgba(247, 251, 253, 0.72)" : "var(--color-text-muted)";

  const handleToggle = (key: string) => {
    const existing = reactions.find((r) => r.key === key);
    // Reactions are best-effort; a failure must not crash the timeline.
    void toggleReaction(client, roomId, message.id, key, existing).catch(() => undefined);
  };

  const trigger = (
    <div style={{ position: "relative", alignSelf: "center" }}>
      <button
        type="button"
        aria-label="React"
        title="React"
        onClick={() => setPickerOpen((p) => !p)}
        style={{
          opacity: hover || pickerOpen ? 1 : 0,
          transition: "opacity .12s ease",
          width: 28,
          height: 28,
          borderRadius: "var(--radius-pill)",
          border: "1px solid var(--color-border)",
          background: "var(--color-panel)",
          boxShadow: "var(--shadow-sm)",
          cursor: "pointer",
          fontSize: 14,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        🙂
      </button>
      {pickerOpen && (
        <QuickReactRow
          side={mine ? "right" : "left"}
          onPick={(e) => {
            setPickerOpen(false);
            handleToggle(e);
          }}
        />
      )}
    </div>
  );

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setPickerOpen(false);
      }}
      style={{
        display: "flex",
        justifyContent: mine ? "flex-end" : "flex-start",
        alignItems: "center",
        gap: 6,
        padding: "2px var(--space-lg)",
      }}
    >
      {mine && trigger}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: mine ? "flex-end" : "flex-start",
          maxWidth: "min(75%, 520px)",
        }}
      >
        <div
          style={{
            background: mine ? "var(--color-bubble-out)" : "var(--color-bubble-in)",
            color: bodyColor,
            borderRadius: "var(--radius-md)",
            padding: "var(--space-sm) var(--space-md)",
            boxShadow: "var(--shadow-sm)",
            maxWidth: "100%",
          }}
        >
          {senderLabel && !mine && (
            <div
              style={{
                fontSize: "var(--font-size-sm)",
                fontWeight: 600,
                color: senderLabel.color,
                marginBottom: 2,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {senderLabel.name}
            </div>
          )}
          {message.kind === "audio" && message.audio ? (
            <AudioPlayer audio={message.audio} mine={mine} />
          ) : (
            <Text style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", color: bodyColor }}>
              {message.body}
            </Text>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 4,
              marginTop: 2,
            }}
          >
            <Text size="sm" style={{ color: metaColor }}>
              {formatClock(message.ts)}
            </Text>
            {mine && (
              <span
                aria-label={read ? "Read" : "Sent"}
                title={read ? "Read" : "Sent"}
                style={{
                  fontSize: 12,
                  color: read ? "var(--color-receipt-read)" : "var(--color-receipt-sent)",
                }}
              >
                ✓✓
              </span>
            )}
          </div>
        </div>
        <Reactions reactions={reactions} onToggle={handleToggle} align={mine ? "right" : "left"} />
      </div>
      {!mine && trigger}
    </div>
  );
}
